import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { buyersAPI, analyticsAPI } from '../services/api';
import {
  Users,
  Mail,
  Target,
  BarChart3,
  Clock,
  Activity,
  FileText
} from 'lucide-react';
import OnboardingCard from './OnboardingCard';
import { useOnboarding } from '../contexts/OnboardingContext';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const { refresh: refreshOnboarding } = useOnboarding();
  const [stats, setStats] = useState(null);
  const [providerStats, setProviderStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [buyersResponse, providerStatsResponse] = await Promise.all([
        buyersAPI.getStatistics(),
        analyticsAPI.getProviderStats(30).catch(() => ({ data: null }))
      ]);

      setStats(buyersResponse.data.statistics);
      setProviderStats(providerStatsResponse?.data || null);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Proofs that appear after emails go out (sender confirmed by the worker) show up without a reload
  useEffect(() => {
    refreshOnboarding();
  }, [refreshOnboarding]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="alert alert-error">{error}</div>
        <button onClick={loadDashboardData} className="btn btn-primary">
          Retry
        </button>
      </div>
    );
  }

  // Derive summary from whichever email provider has data (Resend first, SendGrid fallback)
  const providers = providerStats?.providers;
  const resend = providers?.resend?.configured && !providers?.resend?.error ? providers.resend : null;
  const sg     = providers?.sendgrid?.configured && !providers?.sendgrid?.error ? providers.sendgrid : null;
  const activeProv = resend || sg;

  const totalSent    = resend?.total ?? sg?.totals?.requests ?? null;
  const deliveryRate = activeProv?.rates?.delivery_rate ?? null;

  const fmtRate = (val) => (val != null ? `${Number(val).toFixed(1)}%` : '—');
  const fmtNum  = (val) => (val != null ? Number(val).toLocaleString() : '—');

  const metricCards = [
    {
      title: 'Total Prospects',
      value: stats?.total_buyers?.toLocaleString() || '0',
      sub: 'In your database',
      icon: Users,
      color: '#7c3aed'
    },
    {
      title: 'Emails Sent',
      value: fmtNum(totalSent),
      sub: 'Last 30 days',
      icon: Mail,
      color: '#15803d'
    },
    {
      title: 'Delivery Rate',
      value: fmtRate(deliveryRate),
      sub: 'Via email providers',
      icon: Target,
      color: '#b45309'
    }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">
            <BarChart3 className="title-icon" />
            Dashboard Overview
          </h1>
          <p className="dashboard-subtitle">
            Live metrics from your email providers
          </p>
        </div>
        <div className="header-info">
          <div className="last-updated">
            <Clock className="clock-icon" />
            <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
          </div>
          <button onClick={loadDashboardData} className="refresh-btn">
            <Activity className="refresh-icon" />
            Refresh
          </button>
        </div>
      </div>

      <OnboardingCard />

      <div className="metrics-grid">
        {metricCards.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="metric-card">
              <div className="metric-header">
                <div
                  className="metric-icon-container"
                  style={{ backgroundColor: `${metric.color}20` }}
                >
                  <Icon
                    className="metric-icon"
                    style={{ color: metric.color }}
                  />
                </div>
              </div>
              <div className="metric-content">
                <div className="metric-value">{metric.value}</div>
                <div className="metric-title">{metric.title}</div>
                <div className="metric-sub">{metric.sub}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3 className="card-title">Quick Actions</h3>
          <div className="quick-actions">
            <button className="action-btn" onClick={() => navigate('/search')}>
              <Users className="action-icon" />
              <span>Import Prospects</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/campaigns')}>
              <Target className="action-icon" />
              <span>Create Campaign</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/templates')}>
              <FileText className="action-icon" />
              <span>Templates</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/analytics')}>
              <BarChart3 className="action-icon" />
              <span>View Analytics</span>
            </button>
          </div>
        </div>

        <div className="dashboard-card">
          <h3 className="card-title">Top Industries</h3>
          <div className="industries-list">
            {stats?.by_industry && Object.entries(stats.by_industry)
              .sort(([,a], [,b]) => b - a)
              .slice(0, 5)
              .map(([industry, count], index) => (
                <div key={industry} className="industry-item">
                  <div className="industry-rank">{index + 1}</div>
                  <div className="industry-info">
                    <div className="industry-name">{industry}</div>
                    <div className="industry-count">{count} leads</div>
                  </div>
                  <div className="industry-percentage">
                    {Math.round((count / stats.total_buyers) * 100)}%
                  </div>
                </div>
              ))}
            {(!stats?.by_industry || Object.keys(stats.by_industry).length === 0) && (
              <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>No industry data yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
